import React, { useEffect, useState, useRef, Fragment } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../lib/LanguageContext';
import { translations } from '../../lib/translations';
// ─── Parabola math helpers ────────────────────────────────────────────────────
function parabolaY(x: number, a = 0.04, b = 0, c = 80): number {
  return a * x * x + b * x + c;
}
const ALL_DOTS = [-90, -75, -60, -45, -30, -15, 0, 15, 30, 45, 60, 75, 90, -50, 50, -20, 20];
// ─── Statistical Parabola — looping ──────────────────────────────────────────
function StatisticalParabola() {
  const ref = useRef<SVGSVGElement>(null);
  const inView = useInView(ref, {
    once: false,
    margin: '-80px'
  });
  const [phase, setPhase] = useState(0);
  const [loopCount, setLoopCount] = useState(0);
  const [counter, setCounter] = useState(1);
  useEffect(() => {
    if (!inView) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => timers.push(setTimeout(fn, ms));
    setPhase(0);
    setCounter(1);
    t(() => setPhase(1), 400); // parabola appears
    t(() => setPhase(2), 1000); // first dots
    t(() => setPhase(3), 1800); // more dots
    t(() => setPhase(4), 2600); // cluster begins
    t(() => setPhase(5), 3400); // fully clustered
    t(() => setCounter(c => c + 1), 1200);
    t(() => setCounter(c => c + 1), 2100);
    t(() => setCounter(c => c + 1), 3000);
    t(() => setCounter(c => c + 1), 3900);
    // 1s pause after completion, then reset
    t(() => setLoopCount(c => c + 1), 5400);
    return () => timers.forEach(clearTimeout);
  }, [inView, loopCount]);
  const W = 280,
    H = 200;
  const cx = W / 2,
    cy = 20;
  const points: string[] = [];
  for (let px = -120; px <= 120; px += 4) {
    const py = parabolaY(px);
    points.push(`${cx + px},${cy + py}`);
  }
  const pathD = `M ${points.join(' L ')}`;
  const visibleDotCount = phase === 0 ? 0 : phase === 1 ? 3 : phase === 2 ? 8 : phase === 3 ? 13 : ALL_DOTS.length;
  const clusterFactor = phase >= 4 ? phase >= 5 ? 0.85 : 0.4 : 0;
  return <div className="flex flex-col items-center gap-3" data-id="element-329">
      <svg ref={ref} width={W} height={H + 20} viewBox={`0 0 ${W} ${H + 20}`} className="overflow-visible" data-id="element-330">
        {/* Parabola curve */}
        <motion.path d={pathD} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: phase >= 1 ? 1 : 0,
        opacity: phase >= 1 ? 1 : 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-331" />
        {/* Vertex marker */}
        <motion.circle cx={cx} cy={cy + parabolaY(0)} r="3" fill="rgba(255,255,255,0.4)" initial={{
        opacity: 0
      }} animate={{
        opacity: phase >= 1 ? 1 : 0
      }} transition={{
        duration: 0.4,
        delay: 0.6
      }} data-id="element-332" />
        {/* Sampling dots */}
        {ALL_DOTS.slice(0, visibleDotCount).map((dx, i) => {
        const targetX = dx * (1 - clusterFactor);
        const screenX = cx + targetX;
        const screenY = cy + parabolaY(targetX);
        return <motion.circle key={i} cx={screenX} cy={screenY} r="2.5" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" initial={{
          opacity: 0,
          scale: 0
        }} animate={{
          opacity: 1,
          scale: 1,
          cx: screenX,
          cy: screenY
        }} transition={{
          duration: 0.5,
          delay: i * 0.04,
          ease: 'easeOut'
        }} data-id="element-333" />;
      })}
      </svg>
      {/* Counter */}
      <motion.div className="font-mono text-xs tracking-widest" style={{
      color: 'var(--grey-500)'
    }} animate={{
      opacity: phase >= 2 ? 1 : 0
    }} transition={{
      duration: 0.4
    }} data-id="element-334">
        n → n+{counter} → n+{counter + 1} → n+{counter + 2} → …
      </motion.div>
    </div>;
}
// ─── Mechanistic Derivation — looping ────────────────────────────────────────
function MechanisticDerivation() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: false,
    margin: '-80px'
  });
  const [phase, setPhase] = useState(0);
  const [loopCount, setLoopCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => timers.push(setTimeout(fn, ms));
    setPhase(0);
    t(() => setPhase(1), 400); // equation 1
    t(() => setPhase(2), 1200); // equation 2
    t(() => setPhase(3), 2000); // equation 3
    t(() => setPhase(4), 2800); // vertical line
    // 1s pause after completion, then reset
    t(() => setLoopCount(c => c + 1), 4800);
    return () => timers.forEach(clearTimeout);
  }, [inView, loopCount]);
  const W = 280,
    H = 200;
  const cx = W / 2,
    cy = 20;
  const vertexY = cy + parabolaY(0);
  const points: string[] = [];
  for (let px = -120; px <= 120; px += 4) {
    points.push(`${cx + px},${cy + parabolaY(px)}`);
  }
  const pathD = `M ${points.join(' L ')}`;
  const steps = [{
    text: 'y = ax² + bx + c',
    phase: 1
  }, {
    text: 'dy/dx = 0',
    phase: 2
  }, {
    text: 'x = −b / 2a',
    phase: 3
  }];
  return <div ref={ref} className="flex flex-col items-center gap-3" data-id="element-335">
      {/* Equation steps */}
      <div className="flex flex-col items-center gap-1 min-h-[88px] justify-center" data-id="element-336">
        {steps.map((s, i) => <div key={i} className="flex flex-col items-center" data-id="element-337">
            <motion.div initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: phase >= s.phase ? 1 : 0,
          y: phase >= s.phase ? 0 : 6
        }} transition={{
          duration: 0.5
        }} className="font-mono text-sm tracking-wide" style={{
          color: 'rgba(255,255,255,0.65)'
        }} data-id="element-338">
              {s.text}
            </motion.div>
            {i < steps.length - 1 && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: phase >= s.phase + 1 ? 1 : 0
        }} transition={{
          duration: 0.3
        }} className="text-xs my-0.5" style={{
          color: 'rgba(255,255,255,0.2)'
        }} data-id="element-339">
                ↓
              </motion.div>}
          </div>)}
      </div>

      {/* Parabola with vertical line */}
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible" data-id="element-340">
        <motion.path d={pathD} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: phase >= 1 ? 1 : 0,
        opacity: phase >= 1 ? 1 : 0
      }} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} data-id="element-341" />
        <motion.circle cx={cx} cy={vertexY} r="3.5" fill="rgba(255,255,255,0.7)" initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: phase >= 3 ? 1 : 0,
        scale: phase >= 3 ? 1 : 0
      }} transition={{
        duration: 0.4
      }} data-id="element-342" />
        <motion.line x1={cx} y1={20} x2={cx} y2={vertexY} stroke="rgba(255,255,255,0.45)" strokeWidth="1" strokeDasharray="4 3" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: phase >= 4 ? 1 : 0,
        opacity: phase >= 4 ? 1 : 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-343" />
      </svg>
    </div>;
}
// ─── Apple Drop — looping ────────────────────────────────────────────────────
function AppleDrop() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: false,
    margin: '-80px'
  });
  const [phase, setPhase] = useState(0);
  const [loopCount, setLoopCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => timers.push(setTimeout(fn, ms));
    setPhase(0);
    t(() => setPhase(1), 600);
    t(() => setPhase(2), 1400);
    t(() => setPhase(3), 2600);
    t(() => setLoopCount(c => c + 1), 4600);
    return () => timers.forEach(clearTimeout);
  }, [inView, loopCount]);
  const appleY = phase >= 2 ? phase >= 3 ? 280 : 160 : 0;
  const appleOpacity = phase >= 3 ? 0 : phase >= 1 ? 1 : 0;
  return <div ref={ref} style={{
    height: '320px',
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center'
  }} data-id="element-344">
      {/* Branch */}
      <svg width="160" height="40" viewBox="0 0 160 40" style={{
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)'
    }} data-id="element-345">
        <line x1="0" y1="16" x2="160" y2="16" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" data-id="element-346" />
        <path d="M 80 16 Q 80 28 76 32" stroke="rgba(255,255,255,0.3)" strokeWidth="1.2" fill="none" data-id="element-347" />
      </svg>

      {/* Apple — 80px wide */}
      <motion.div style={{
      position: 'absolute',
      top: '40px',
      left: '50%',
      x: '-50%'
    }} animate={{
      y: appleY,
      opacity: appleOpacity
    }} transition={{
      y: {
        duration: phase >= 2 ? phase >= 3 ? 0.5 : 1.0 : 0,
        ease: 'easeIn'
      },
      opacity: {
        duration: 0.3
      }
    }} data-id="element-348">
        <svg width="80" height="90" viewBox="0 0 64 72" data-id="element-349">
          <path d="M 32 6 Q 36 1 41 3" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" fill="none" data-id="element-350" />
          <path d="M 34 5 Q 42 -1 44 6 Q 37 8 34 5" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" fill="none" data-id="element-351" />
          <path d="M 32 10 C 16 10 6 20 6 34 C 6 50 16 62 32 62 C 48 62 58 50 58 34 C 58 20 48 10 32 10 Z" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2" fill="rgba(255,255,255,0.03)" data-id="element-352" />
          <path d="M 28 12 Q 32 8 36 12" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" fill="none" data-id="element-353" />
        </svg>
      </motion.div>
    </div>;
}
// ─── F=ma Sequence — looping ─────────────────────────────────────────────────
const BG_EQUATIONS = ['F = −kx', 'E = mc²', 'PV = nRT', '∇²φ = ρ/ε₀', 'S = k ln Ω', 'Ĥψ = Eψ', 'ds² = −c²dt²', 'dS/dt ≥ 0', '∮ B·dA = 0', 'F = qv × B', 'ΔxΔp ≥ ℏ/2', 'E = hν'];
function FmaSequence() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: false,
    margin: '-80px'
  });
  const [phase, setPhase] = useState(0);
  const [loopCount, setLoopCount] = useState(0);
  const {
    lang
  } = useLanguage();
  const t = translations[lang].searchOrDerive;
  useEffect(() => {
    if (!inView) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const timer = (fn: () => void, ms: number) => timers.push(setTimeout(fn, ms));
    setPhase(0);
    timer(() => setPhase(1), 500);
    timer(() => setPhase(2), 2400);
    timer(() => setPhase(3), 4200);
    timer(() => setPhase(4), 6000);
    timer(() => setLoopCount(c => c + 1), 8000);
    return () => timers.forEach(clearTimeout);
  }, [inView, loopCount]);
  const showBg = phase === 1 || phase === 2;
  const showManyFormulas = phase >= 1;
  const showNewton = phase >= 2;
  const showFma = phase >= 3;
  const showMechanism = phase >= 4;
  return <div ref={ref} className="relative flex flex-col items-center justify-center" style={{
    minHeight: '400px'
  }} data-id="element-354">
      {/* Background equations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none flex items-center justify-center" data-id="element-355">
        {BG_EQUATIONS.map((eq, i) => {
        const angle = i / BG_EQUATIONS.length * 360;
        const radius = 160 + i % 3 * 55;
        const x = 50 + Math.cos(angle * Math.PI / 180) * (radius / 5.5);
        const y = 50 + Math.sin(angle * Math.PI / 180) * (radius / 9);
        return <motion.span key={`${loopCount}-${i}`} className="absolute font-mono text-xs select-none" style={{
          left: `${x}%`,
          top: `${y}%`,
          transform: 'translate(-50%, -50%)',
          color: 'rgba(255,255,255,0.08)'
        }} initial={{
          opacity: 0
        }} animate={{
          opacity: showBg ? 1 : 0
        }} transition={{
          duration: 0.8,
          delay: i * 0.05
        }} data-id="element-356">
              {eq}
            </motion.span>;
      })}
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center gap-5 text-center" data-id="element-357">
        <motion.p style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontSize: '0.9375rem',
        color: 'rgba(255,255,255,0.65)',
        lineHeight: 2.0,
        textAlign: 'center'
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: showManyFormulas ? 1 : 0
      }} transition={{
        duration: 0.8
      }} data-id="element-358">
          {t.fmaLine1.split('\n').map((line, i) => <Fragment key={i} data-id="element-359">
              {line}
              {i === 0 && <br data-id="element-360" />}
            </Fragment>)}
        </motion.p>

        <motion.p style={{
        fontFamily: '"IBM Plex Sans", sans-serif',
        fontSize: '0.9375rem',
        color: 'rgba(255,255,255,0.7)',
        lineHeight: 2.0,
        textAlign: 'center'
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: showNewton ? 1 : 0
      }} transition={{
        duration: 0.8
      }} data-id="element-361">
          {t.fmaLine2}
        </motion.p>

        {/* F = ma — dominant */}
        <motion.div style={{
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: 'clamp(88px, 14vw, 144px)',
        color: 'var(--white)',
        letterSpacing: '-0.02em',
        lineHeight: 1,
        textAlign: 'center',
        whiteSpace: 'nowrap'
      }} initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: showFma ? 1 : 0,
        scale: showFma ? 1 : 0.9
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-362">
          F = ma
        </motion.div>

        {/* MECHANISM REPLACES APPROXIMATION */}
        <motion.p style={{
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: '0.65rem',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.3)',
        textAlign: 'center'
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: showMechanism ? 1 : 0
      }} transition={{
        duration: 1
      }} data-id="element-363">
          {t.fmaTag}
        </motion.p>
      </div>
    </div>;
}
// ─── Main Section ─────────────────────────────────────────────────────────────
export function SearchOrDeriveSection() {
  const headerRef = useRef<HTMLDivElement>(null);
  const headerInView = useInView(headerRef, {
    once: true,
    margin: '-80px'
  });
  const transRef = useRef<HTMLDivElement>(null);
  const transInView = useInView(transRef, {
    once: true,
    margin: '-60px'
  });
  const appleHeaderRef = useRef<HTMLDivElement>(null);
  const appleHeaderInView = useInView(appleHeaderRef, {
    once: true,
    margin: '-60px'
  });
  const finalRef = useRef<HTMLDivElement>(null);
  const finalInView = useInView(finalRef, {
    once: true,
    margin: '-60px'
  });
  const {
    lang
  } = useLanguage();
  const t = translations[lang].searchOrDerive;
  return <section style={{
    width: '100%',
    backgroundColor: 'var(--black)',
    color: 'var(--white)',
    padding: '10rem 1.5rem',
    overflow: 'hidden'
  }} data-id="element-364">
      <div style={{
      maxWidth: '56rem',
      margin: '0 auto'
    }} data-id="element-365">
        {/* ── Section Header ── */}
        <motion.div ref={headerRef} className="flex flex-col items-center text-center" style={{
        marginBottom: '8rem'
      }} initial={{
        opacity: 0,
        y: 24
      }} animate={headerInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-366">
          <h2 style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 300,
          fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
          letterSpacing: '-0.02em',
          color: 'var(--white)',
          marginBottom: '2rem',
          lineHeight: 1.05
        }} data-id="element-367">
            {t.title}
          </h2>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.25rem'
        }} data-id="element-368">
            <span style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: 'var(--grey-500)'
          }} data-id="element-369">
              {t.statLabel}
            </span>
            <span style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.6rem',
            color: 'rgba(255,255,255,0.15)',
            letterSpacing: '0.15em'
          }} data-id="element-370">
              {t.vs}
            </span>
            <span style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: 'var(--red)'
          }} data-id="element-371">
              {t.mechLabel}
            </span>
          </div>
        </motion.div>

        {/* ── PART A: Two-column comparison ── */}
        <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '4rem',
        marginBottom: '8rem'
      }} data-id="element-372">
          {/* LEFT — Statistical */}
          <div className="flex flex-col items-center text-center gap-6" data-id="element-373">
            <h3 style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '0.75rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--grey-500)'
          }} data-id="element-374">
              {t.statLabel}
            </h3>
            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.35rem',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.9375rem',
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            lineHeight: 1.7
          }} data-id="element-375">
              {t.statSteps.map((step, i) => <p key={i} data-id="element-376">{step}</p>)}
            </div>
            <div style={{
            minHeight: '88px'
          }} data-id="element-377" />
            <StatisticalParabola data-id="element-378" />
            <div style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--grey-500)',
            border: '1px solid rgba(255,255,255,0.08)',
            padding: '0.5rem 1rem'
          }} data-id="element-379">
              {t.statTag}
            </div>
          </div>

          {/* RIGHT — Mechanistic */}
          <div className="flex flex-col items-center text-center gap-6" data-id="element-380">
            <h3 style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '0.75rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--red)'
          }} data-id="element-381">
              {t.mechLabel}
            </h3>
            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.35rem',
            color: 'rgba(255,255,255,0.5)',
            fontSize: '0.9375rem',
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            lineHeight: 1.7
          }} data-id="element-382">
              {t.mechSteps.map((step, i) => <p key={i} data-id="element-383">{step}</p>)}
            </div>
            <MechanisticDerivation data-id="element-384" />
            <div style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--red)',
            border: '1px solid rgba(196,30,30,0.25)',
            padding: '0.5rem 1rem'
          }} data-id="element-385">
              {t.mechTag}
            </div>
          </div>
        </div>

        {/* ── Transition ── */}
        <motion.div ref={transRef} className="flex flex-col items-center text-center" style={{
        marginBottom: '8rem'
      }} initial={{
        opacity: 0
      }} animate={transInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 1
      }} data-id="element-386">
          <div style={{
          width: '1px',
          height: '4rem',
          backgroundColor: 'rgba(255,255,255,0.08)',
          marginBottom: '2.5rem'
        }} data-id="element-387" />
          <p style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 300,
          fontSize: 'clamp(1rem, 2vw, 1.25rem)',
          color: 'rgba(255,255,255,0.35)',
          lineHeight: 1.7,
          whiteSpace: 'pre-line'
        }} data-id="element-388">
            {t.transition}
          </p>
          <div style={{
          width: '1px',
          height: '4rem',
          backgroundColor: 'rgba(255,255,255,0.08)',
          marginTop: '2.5rem'
        }} data-id="element-389" />
        </motion.div>

        {/* ── PART B: Apple + F=ma ── */}
        <motion.div ref={appleHeaderRef} className="flex flex-col items-center text-center" style={{
        marginBottom: '4rem'
      }} initial={{
        opacity: 0,
        y: 16
      }} animate={appleHeaderInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-390">
          <p style={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.65rem',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: 'var(--grey-500)',
          marginBottom: '1rem'
        }} data-id="element-391">
            {t.newtonLabel}
          </p>
        </motion.div>

        {/* Two-column: Apple | F=ma sequence */}
        <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '4rem',
        marginBottom: '8rem',
        alignItems: 'center'
      }} data-id="element-392">
          <AppleDrop data-id="element-393" />
          <div className="flex flex-col items-center" data-id="element-394">
            <FmaSequence data-id="element-395" />
          </div>
        </div>

        {/* ── Final Declaration ── */}
        <motion.div ref={finalRef} className="flex flex-col items-center text-center" initial={{
        opacity: 0,
        y: 20
      }} animate={finalInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 1,
        ease: [0.16, 1, 0.3, 1]
      }} data-id="element-396">
          <p style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 300,
          fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
          color: 'rgba(255,255,255,0.55)',
          lineHeight: 1.8,
          maxWidth: '38rem'
        }} data-id="element-397">
            {t.final1}
            <span style={{
            color: 'var(--red)',
            fontWeight: 400
          }} data-id="element-398">
              {t.final2}
            </span>
          </p>
        </motion.div>
      </div>
    </section>;
}